/** Mieterportal 2.0 (MP-D): Aktionen des Mieters – die Kasse rechnet sie beim Scannen automatisch. */
import { z } from "zod";

export const AKTION_ARTEN = ["rabatt", "bundle", "staffel"] as const;
export type AktionArt = (typeof AKTION_ARTEN)[number];

const Tag = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const Prozent = z.number().int().min(1).max(90);

export const StaffelStufe = z.object({ tage: z.number().int().min(1).max(365), prozent: Prozent });
export type StaffelStufe = z.infer<typeof StaffelStufe>;

export const STAFFEL_STANDARD: StaffelStufe[] = [
  { tage: 30, prozent: 10 },
  { tage: 60, prozent: 25 },
];

export const AktionAnlegen = z
  .object({
    art: z.enum(AKTION_ARTEN),
    titel: z.string().trim().min(1).max(60),
    /** Rabatt und Bundle: Prozent auf den Artikelpreis. */
    prozent: Prozent.default(10),
    /** Leer = alle eigenen Artikel, sonst nur diese Kategorie. */
    kategorie: z.string().trim().max(40).default(""),
    /** Gültigkeit (Kalendertage, Europe/Berlin). `bis` leer = bis zum Beenden. */
    von: Tag,
    bis: Tag.nullable().default(null),
    /** Bundle: ab so vielen eigenen Teilen im selben Warenkorb. */
    bundleAnzahl: z.number().int().min(2).max(20).default(3),
    /** Staffel: nach so vielen Tagen im Regal so viel Prozent (aufsteigend). */
    staffel: z.array(StaffelStufe).min(1).max(4).default(STAFFEL_STANDARD),
  })
  .superRefine((a, ctx) => {
    if (a.bis && a.bis < a.von) ctx.addIssue({ code: "custom", path: ["bis"], message: "Das Ende liegt vor dem Start." });
    if (a.art === "staffel") {
      for (let i = 1; i < a.staffel.length; i++) {
        if (a.staffel[i]!.tage <= a.staffel[i - 1]!.tage || a.staffel[i]!.prozent <= a.staffel[i - 1]!.prozent) {
          ctx.addIssue({ code: "custom", path: ["staffel"], message: "Stufen müssen in Tagen und Prozent aufsteigend sein." });
          break;
        }
      }
    }
  });
export type AktionAnlegen = z.infer<typeof AktionAnlegen>;

export interface AktionKurz {
  id: string;
  art: AktionArt;
  titel: string;
  prozent: number;
  kategorie: string;
  von: string;
  bis: string | null;
  bundleAnzahl: number;
  staffel: StaffelStufe[];
}

/** Gültig am Tag? (reine Funktion, auch für Vorschau im Portal) */
export function aktionGueltig(a: { von: string; bis: string | null; beendetAt?: string | null }, heute: string): boolean {
  if (a.beendetAt) return false;
  if (a.von > heute) return false;
  if (a.bis && a.bis < heute) return false;
  return true;
}

/** Prozent, der für einen Artikel aus einer Aktion folgt – 0 wenn nichts greift. `anzahlImKorb` = eigene Teile desselben Mieters (ggf. der Kategorie) im Warenkorb. */
export function aktionProzent(a: AktionKurz, artikel: { kategorie: string; tageImRegal: number }, anzahlImKorb: number): number {
  if (a.kategorie && a.kategorie.toLowerCase() !== artikel.kategorie.toLowerCase()) return 0;
  if (a.art === "rabatt") return a.prozent;
  if (a.art === "bundle") return anzahlImKorb >= a.bundleAnzahl ? a.prozent : 0;
  let p = 0;
  for (const s of a.staffel) if (artikel.tageImRegal >= s.tage) p = s.prozent;
  return p;
}

export function aktionText(a: AktionKurz): string {
  const wo = a.kategorie ? ` auf ${a.kategorie}` : "";
  if (a.art === "rabatt") return `−${a.prozent} %${wo}`;
  if (a.art === "bundle") return `Ab ${a.bundleAnzahl} Teilen${wo} −${a.prozent} %`;
  return a.staffel.map((s) => `ab ${s.tage} Tagen −${s.prozent} %`).join(", ");
}
